<template>
  <div class="dimension-details-container">
    <!-- Left side content -->
    <div class="left-content">
      <h2 class="mb-4">Edit Dimension</h2>

      <!-- Dimension Details -->
      <div class="mb-6 flex flex-col gap-4" v-if="currentDimension">
        <div class="management-row">
          <!-- Basic Information -->
          <div class="p-4 border rounded management-section">
            <h3 class="mb-3">Basic Information</h3>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700"
                >Dimension Value (Identifier)</label
              >
              <InputText v-model="currentDimension.value" class="w-full" :disabled="true" />
              <small class="text-gray-500"
                >This is the unique identifier and cannot be changed</small
              >
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700">Name (EN)</label>
              <InputText v-model="currentDimension.translation.en" class="w-full" />
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700">Name (DE)</label>
              <InputText v-model="currentDimension.translation.de" class="w-full" />
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700">Description (EN)</label>
              <Textarea v-model="currentDimension.description.en" class="w-full" rows="3" />
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700">Description (DE)</label>
              <Textarea v-model="currentDimension.description.de" class="w-full" rows="3" />
            </div>

            <Button
              label="Save Changes"
              icon="pi pi-save"
              @click="saveDimension"
              class="mt-2"
              :loading="saving"
            />
          </div>
        </div>

        <!-- Questions Section -->
        <div class="mb-6">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Questions</h3>
              <p class="text-sm text-gray-600 mt-1">
                Manage questions associated with this dimension
              </p>
            </div>
            <Button
              label="Add Question"
              icon="pi pi-plus"
              class="p-button-primary"
              @click="openNewQuestionDialog"
            />
            <Button
              label="Test Add Question"
              icon="pi pi-plus-circle"
              class="p-button-outlined p-button-secondary ml-2"
              @click="testAddQuestion"
            />
            <Button
              label="Refresh Data"
              icon="pi pi-refresh"
              class="p-button-outlined p-button-info ml-2"
              @click="refreshDimensionData"
            />
          </div>

          <!-- Questions List -->
          <div v-if="currentDimension.questions && currentDimension.questions.length > 0">
            <div class="space-y-4">
              <div
                v-for="(question, index) in currentDimension.questions"
                :key="question._id || index"
                class="bg-white border border-gray-200 rounded-lg shadow-sm
                 hover:shadow-md transition-shadow duration-200"
              >
                <QuestionListItem
                  :question="question"
                  :index="index"
                  :is-expanded="expandedQuestionIndex === index"
                  :has-valid-dimension="true"
                  :selected="selectedQuestionIndices.includes(index)"
                  :selectable="true"
                  @update-question="updateQuestion(index, $event)"
                  @delete-question="deleteQuestion(index)"
                  @toggle-expanded="toggleQuestionExpanded(index)"
                  @toggle-selected="toggleQuestionSelection(index)"
                />
              </div>
            </div>

            <!-- Bulk Actions -->
            <div
              v-if="currentDimension.questions && currentDimension.questions.length > 0"
              class="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4"
            >
              <div class="flex flex-wrap items-center justify-between gap-4">
                <div class="flex items-center gap-2">
                  <Checkbox
                    v-model="selectAllDimensionQuestions"
                    :binary="true"
                  />
                  <span class="text-sm text-gray-600">Select all questions</span>
                </div>
                <div v-if="selectedQuestionIndices.length > 0"
                   class="flex flex-wrap items-center gap-2">
                  <SplitButton
                    label="Set Force Input"
                    icon="pi pi-pencil"
                    :model="bulkForceInputMenuItems"
                    class="p-button-outlined"
                  />
                  <SplitButton
                    label="Set Polarity"
                    icon="pi pi-tag"
                    :model="bulkPolarityMenuItems"
                    class="p-button-outlined"
                  />
                  <Button
                    :label="`Delete Selected (${selectedQuestionIndices.length})`"
                    icon="pi pi-trash"
                    @click="confirmBulkDeleteQuestions"
                    class="p-button-outlined"
                    severity="danger"
                  />
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-8">
            <div class="text-gray-400 mb-2">
              <i class="pi pi-question-circle text-4xl"></i>
            </div>
            <p class="text-gray-500 text-lg">No questions available for this dimension.</p>
            <p class="text-gray-400 text-sm mt-1">
              Click "Add Question" to create the first question
            </p>
          </div>
        </div>
      </div>

      <div v-else>
        <LoadingSpinner></LoadingSpinner>
      </div>
    </div>

    <!-- Right side content -->
    <div class="right-content">
      <template v-if="currentDimension">
        <div class="result-descriptions-section">
          <h3 class="mb-3">Result Descriptions</h3>
          <div class="mb-4">
            <Button
              label="Add Result Description"
              icon="pi pi-plus"
              @click="openResultDescriptionDialog"
            />
          </div>

          <div
            v-if="
              currentDimension.resultDescriptions && currentDimension.resultDescriptions.length > 0
            "
          >
            <div
              v-for="(resultDesc, index) in currentDimension.resultDescriptions"
              :key="index"
              class="mb-3 p-3 border rounded"
            >
              <div class="flex justify-between items-start mb-2">
                <h4>Result Description {{ index + 1 }}</h4>
                <Button
                  icon="pi pi-trash"
                  class="p-button-text p-button-danger"
                  @click="removeResultDescription(index)"
                />
              </div>
              <div class="text-sm">
                <p><strong>Headline (EN):</strong> {{ resultDesc.headline?.en || 'N/A' }}</p>
                <p><strong>Headline (DE):</strong> {{ resultDesc.headline?.de || 'N/A' }}</p>
                <p><strong>Content (EN):</strong> {{ resultDesc.content.en }}</p>
                <p><strong>Content (DE):</strong> {{ resultDesc.content.de }}</p>
                <p><strong>Tip (EN):</strong> {{ resultDesc.tip.en }}</p>
                <p><strong>Tip (DE):</strong> {{ resultDesc.tip.de }}</p>
                <p>
                  <strong>Status:</strong>
                  <span
                    :class="resultDesc.status === 'positive' ? 'text-green-600' : 'text-red-600'"
                  >
                    {{ resultDesc.status }}
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div v-else>
            <p class="text-gray-500">No result descriptions available.</p>
          </div>
        </div>
      </template>
      <div v-else class="loading-container">
        <LoadingSpinner></LoadingSpinner>
      </div>
    </div>

    <!-- Dialogs -->
    <Dialog
      header="Create New Question"
      v-model:visible="newQuestionDialogVisible"
      modal
      class="w-3/4"
      @hide="closeNewQuestionDialog"
    >
      <QuestionEditor
        :question="newQuestion"
        :is-new="true"
        :loading="addingQuestion"
        @save-new="addNewQuestion"
        @cancel="closeNewQuestionDialog"
      />
    </Dialog>

    <Dialog
      header="Add Result Description"
      v-model:visible="resultDescriptionDialogVisible"
      modal
      @hide="closeResultDescriptionDialog"
    >
      <div class="p-4 space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700">Headline (EN)</label>
          <InputText v-model="newResultDescription.headline.en" class="w-full" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Headline (DE)</label>
          <InputText v-model="newResultDescription.headline.de" class="w-full" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Content (EN)</label>
          <Textarea v-model="newResultDescription.content.en" class="w-full" rows="3" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Content (DE)</label>
          <Textarea v-model="newResultDescription.content.de" class="w-full" rows="3" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Tip (EN)</label>
          <Textarea v-model="newResultDescription.tip.en" class="w-full" rows="3" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Tip (DE)</label>
          <Textarea v-model="newResultDescription.tip.de" class="w-full" rows="3" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Status</label>
          <Dropdown
            v-model="newResultDescription.status"
            :options="statusOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Status"
            class="w-full"
          />
        </div>
        <div class="flex justify-end space-x-2">
          <Button label="Cancel" class="p-button-secondary" @click="closeResultDescriptionDialog" />
          <Button label="Add" @click="addResultDescription" />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script>
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import SplitButton from 'primevue/splitbutton';
import QuestionEditor from '../components/QuestionEditor.vue';
import QuestionListItem from '../components/QuestionListItem.vue';
import LoadingSpinner from '../common/LoadingSpinner.vue';
import { mapState, mapActions } from 'vuex';

export default {
  name: 'DimensionDetails',
  components: {
    Button,
    InputText,
    Textarea,
    Dialog,
    Dropdown,
    Checkbox,
    SplitButton,
    QuestionEditor,
    QuestionListItem,
    LoadingSpinner,
  },
  data() {
    return {
      saving: false,
      newQuestionDialogVisible: false,
      resultDescriptionDialogVisible: false,
      expandedQuestionIndex: -1, // Track which question is expanded
      addingQuestion: false, // Loading state for adding questions
      selectedQuestionIndices: [], // indices of selected questions for bulk actions
      newQuestion: {
        properties: {
          dimensionName: '',
          dimension: '',
          content: {
            text_questionProgress: { en: '', de: '' },
            text_question: { en: '', de: '' },
          },
          options: { forceInput: false },
          data: {},
        },
        class: '',
        type: '',
        viewTimeLogs: [],
      },
      newResultDescription: {
        headline: { en: '', de: '' },
        content: { en: '', de: '' },
        tip: { en: '', de: '' },
        status: 'positive',
      },
      statusOptions: [
        { label: 'Positive', value: 'positive' },
        { label: 'Negative', value: 'negative' },
      ],
    };
  },
  computed: {
    ...mapState('dimensions', ['currentDimension']),
    selectAllDimensionQuestions: {
      get() {
        const questions = this.currentDimension?.questions || [];
        return questions.length > 0 && this.selectedQuestionIndices.length === questions.length;
      },
      set(value) {
        const questions = this.currentDimension?.questions || [];
        this.selectedQuestionIndices = value ? questions.map((_, i) => i) : [];
      },
    },
    bulkForceInputMenuItems() {
      return [
        { label: 'Enable', icon: 'pi pi-check', command: () => this.bulkUpdateSelectedDimensionQuestions('forceInput', true) },
        { label: 'Disable', icon: 'pi pi-times', command: () => this.bulkUpdateSelectedDimensionQuestions('forceInput', false) },
      ];
    },
    bulkPolarityMenuItems() {
      return [
        { label: 'Positive', icon: 'pi pi-thumbs-up', command: () => this.bulkUpdateSelectedDimensionQuestions('positive', true) },
        { label: 'Negative', icon: 'pi pi-thumbs-down', command: () => this.bulkUpdateSelectedDimensionQuestions('positive', false) },
      ];
    },
  },
  async created() {
    const dimensionId = this.$route.params.id;
    console.log('DimensionDetails created with ID:', dimensionId);
    await this.$store.dispatch('dimensions/fetchDimensionById', dimensionId);

    // Debug: Log what we actually received
    console.log('Current dimension after fetch:', this.currentDimension);
    console.log('Questions array:', this.currentDimension?.questions);
    console.log('Questions length:', this.currentDimension?.questions?.length);

    this.initializeNewQuestion();
  },
  beforeRouteLeave(to, from, next) {
    // Clear the current dimension when leaving the edit view
    this.$store.commit('dimensions/CLEAR_CURRENT_DIMENSION');
    next();
  },
  methods: {
    ...mapActions('dimensions', ['fetchDimensionById', 'saveDimension']),

    async saveDimension() {
      this.saving = true;
      try {
        // Create a clean object with only the fields that should be sent to the backend
        const dimensionToSave = {
          _id: this.currentDimension._id, // Include _id so it uses PATCH instead of POST
          translation: this.currentDimension.translation,
          description: this.currentDimension.description,
        };

        // Add resultDescriptions if they exist, but remove _id properties
        if (
          this.currentDimension.resultDescriptions &&
          this.currentDimension.resultDescriptions.length > 0
        ) {
          dimensionToSave.resultDescriptions = this.currentDimension.resultDescriptions.map(
            (desc) => ({
              content: desc.content,
              tip: desc.tip,
              status: desc.status,
            }),
          );
        }

        await this.$store.dispatch('dimensions/saveDimension', dimensionToSave);
        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Dimension saved successfully',
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save dimension',
          life: 3000,
        });
      } finally {
        this.saving = false;
      }
    },

    initializeNewQuestion() {
      this.newQuestion = {
        properties: {
          dimensionName: this.currentDimension?.value || '',
          dimension: this.currentDimension?.value || '',
          content: {
            text_questionProgress: { en: '', de: '' },
            text_question: { en: '', de: '' },
          },
          options: { forceInput: false },
          data: {},
        },
        class: '',
        type: '',
        viewTimeLogs: [],
      };
    },

    openNewQuestionDialog() {
      this.initializeNewQuestion();
      this.newQuestionDialogVisible = true;
    },

    closeNewQuestionDialog() {
      this.newQuestionDialogVisible = false;
    },

    async addNewQuestion(questionData) {
      this.addingQuestion = true;
      try {
        await this.$store.dispatch('dimensions/addDimensionQuestion', {
          dimensionId: this.currentDimension._id,
          questionData,
        });
        this.closeNewQuestionDialog();
        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Question added successfully',
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to add question',
          life: 3000,
        });
      } finally {
        this.addingQuestion = false;
      }
    },

    async updateQuestion(index, questionData) {
      try {
        await this.$store.dispatch('dimensions/updateDimensionQuestion', {
          dimensionId: this.currentDimension._id,
          questionIndex: index,
          questionData,
        });
        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Question updated successfully',
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update question',
          life: 3000,
        });
      }
    },

    async deleteQuestion(index) {
      try {
        await this.$store.dispatch('dimensions/deleteDimensionQuestion', {
          dimensionId: this.currentDimension._id,
          questionIndex: index,
        });
        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Question deleted successfully',
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete question',
          life: 3000,
        });
      }
    },

    toggleQuestionExpanded(index) {
      if (this.expandedQuestionIndex === index) {
        this.expandedQuestionIndex = -1; // Collapse
      } else {
        this.expandedQuestionIndex = index; // Expand this question
      }
    },

    toggleQuestionSelection(index) {
      const idx = this.selectedQuestionIndices.indexOf(index);
      if (idx === -1) {
        this.selectedQuestionIndices = [...this.selectedQuestionIndices, index];
      } else {
        this.selectedQuestionIndices = this.selectedQuestionIndices.filter((i) => i !== index);
      }
    },

    confirmBulkDeleteQuestions() {
      this.$confirm.require({
        message: `Are you sure you want to delete ${this.selectedQuestionIndices.length} selected question(s)?`,
        header: 'Confirm Bulk Delete',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.bulkDeleteDimensionQuestions();
        },
      });
    },

    async bulkDeleteDimensionQuestions() {
      const { _id: dimensionId } = this.currentDimension;
      // Delete in descending index order so indices remain valid
      const sorted = [...this.selectedQuestionIndices].sort((a, b) => b - a);
      try {
        await sorted.reduce(
          (promise, questionIndex) => promise.then(() => this.$store.dispatch('dimensions/deleteDimensionQuestion', {
            dimensionId,
            questionIndex,
          })),
          Promise.resolve(),
        );
        this.selectedQuestionIndices = [];
        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Selected questions deleted successfully',
          life: 3000,
        });
      } catch (error) {
        console.error('Bulk delete failed:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete some questions',
          life: 3000,
        });
      }
    },

    async bulkUpdateSelectedDimensionQuestions(field, value) {
      if (this.selectedQuestionIndices.length === 0) return;
      const { _id: dimensionId } = this.currentDimension;
      try {
        const buildPatch = (question) => {
          const opts = question?.properties?.options || {};
          const data = question?.properties?.data || {};
          return field === 'forceInput'
            ? { properties: { ...question?.properties, options: { ...opts, forceInput: value } } }
            : { properties: { ...question?.properties, data: { ...data, positive: value } } };
        };
        await this.selectedQuestionIndices.reduce(
          (promise, questionIndex) => promise.then(() => {
            const { questions } = this.currentDimension;
            const question = questions[questionIndex];
            const patch = buildPatch(question);
            const questionData = { ...question, ...patch };
            return this.$store.dispatch('dimensions/updateDimensionQuestion', {
              dimensionId,
              questionIndex,
              questionData,
            });
          }),
          Promise.resolve(),
        );
        this.$toast.add({
          severity: 'success',
          summary: 'Bulk Update',
          detail: `Updated ${this.selectedQuestionIndices.length} question(s)`,
          life: 3000,
        });
      } catch (error) {
        console.error('Bulk update failed:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update selected questions',
          life: 3000,
        });
      }
    },

    getQuestionValidationErrors(question) {
      const errors = [];

      if (!question.properties?.dimensionName?.trim()) {
        errors.push('Dimension Name');
      }
      if (!question.properties?.dimension?.trim()) {
        errors.push('Dimension Identifier');
      }
      if (!question.properties?.content?.text_question?.en?.trim()) {
        errors.push('Question Text (EN)');
      }
      if (!question.properties?.content?.text_question?.de?.trim()) {
        errors.push('Question Text (DE)');
      }
      if (!question.class) {
        errors.push('Platform Class');
      }
      if (!question.type) {
        errors.push('Question Type');
      }

      return errors;
    },

    async testAddQuestion() {
      console.log('Testing question addition...');
      const testQuestion = {
        properties: {
          dimensionName: this.currentDimension.value,
          dimension: this.currentDimension.value,
          content: {
            text_questionProgress: { en: 'Test Question 1 of 1', de: 'Test Frage 1 von 1' },
            text_question: {
              en: 'This is a test question to verify the system works.',
              de: 'Dies ist eine Testfrage, um zu überprüfen, ob das System funktioniert.',
            },
          },
          options: { forceInput: false },
          data: {},
        },
        class: 'question_element',
        type: 'rangeSlider',
      };

      try {
        console.log('Adding test question:', testQuestion);
        await this.$store.dispatch('dimensions/addDimensionQuestion', {
          dimensionId: this.currentDimension._id,
          questionData: testQuestion,
        });

        console.log('Test question added successfully');
        console.log('Current dimension after adding question:', this.currentDimension);
        console.log('Questions array after adding:', this.currentDimension?.questions);

        this.$toast.add({
          severity: 'success',
          summary: 'Test Success',
          detail: 'Test question added successfully',
          life: 3000,
        });
      } catch (error) {
        console.error('Failed to add test question:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Test Error',
          detail: `Failed to add test question: ${error.message}`,
          life: 5000,
        });
      }
    },

    async refreshDimensionData() {
      console.log('Refreshing dimension data...');
      try {
        await this.$store.dispatch('dimensions/fetchDimensionById', this.currentDimension._id);
        console.log('Dimension data refreshed');
        console.log('Current dimension after refresh:', this.currentDimension);
        console.log('Questions array after refresh:', this.currentDimension?.questions);
        console.log('Questions length after refresh:', this.currentDimension?.questions?.length);

        this.$toast.add({
          severity: 'success',
          summary: 'Refresh Success',
          detail: 'Dimension data refreshed',
          life: 3000,
        });
      } catch (error) {
        console.error('Failed to refresh dimension data:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Refresh Error',
          detail: `Failed to refresh: ${error.message}`,
          life: 5000,
        });
      }
    },

    openResultDescriptionDialog() {
      this.newResultDescription = {
        headline: { en: '', de: '' },
        content: { en: '', de: '' },
        tip: { en: '', de: '' },
        status: 'positive',
      };
      this.resultDescriptionDialogVisible = true;
    },

    closeResultDescriptionDialog() {
      this.resultDescriptionDialogVisible = false;
    },

    async addResultDescription() {
      try {
        await this.$store.dispatch('dimensions/addResultDescription', {
          dimensionId: this.currentDimension._id,
          resultDescription: this.newResultDescription,
        });
        this.closeResultDescriptionDialog();
        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Result description added successfully',
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to add result description',
          life: 3000,
        });
      }
    },

    async removeResultDescription(index) {
      try {
        await this.$store.dispatch('dimensions/removeResultDescription', {
          dimensionId: this.currentDimension._id,
          index,
        });
        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Result description removed successfully',
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to remove result description',
          life: 3000,
        });
      }
    },
  },
};
</script>

<style scoped>
.dimension-details-container {
  display: flex;
  gap: 2rem;
  padding: 1rem;
  min-height: calc(100vh - 100px);
}

.left-content {
  flex: 2;
  max-width: 800px;
}

.right-content {
  flex: 1;
  min-width: 300px;
}

.management-section {
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.result-descriptions-section {
  background: white;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
</style>
